import { areIntervalsOverlapping, parseISO, startOfDay } from 'date-fns';
import { Cruise } from '../context/CruiseContext';

export interface CruiseConflictCheck {
  departureDate: string;
  returnDate: string;
  existingCruises: Cruise[];
  excludeCruiseId?: string; // For edit mode, exclude the cruise being edited
}

/**
 * Checks if a new cruise's dates conflict with existing cruises
 * Returns the conflicting cruise if found, null otherwise
 * 
 * Note: Back-to-back cruises (where one ends and another begins on the same day) 
 * are NOT considered conflicts
 */
export const checkCruiseConflict = ({
  departureDate,
  returnDate,
  existingCruises,
  excludeCruiseId
}: CruiseConflictCheck): Cruise | null => {
  const newCruiseInterval = {
    start: parseISO(departureDate),
    end: parseISO(returnDate)
  };

  // Filter out the cruise being edited (if any)
  const cruisesToCheck = excludeCruiseId 
    ? existingCruises.filter(cruise => cruise.id !== excludeCruiseId)
    : existingCruises;

  for (const existingCruise of cruisesToCheck) {
    const existingInterval = {
      start: parseISO(existingCruise.departureDate),
      end: parseISO(existingCruise.returnDate)
    };

    // Check for overlap with inclusive: false
    // This means adjacent intervals (back-to-back cruises) won't be considered overlapping
    const hasOverlap = areIntervalsOverlapping(
      newCruiseInterval,
      existingInterval,
      { inclusive: false }
    );

    if (hasOverlap) {
      return existingCruise;
    }
  }

  return null;
};

/**
 * Calculates the exact end time of a cruise on its return date.
 * If the cruise has an arrival time listed for its final port that is after 9:00 AM, that time is used.
 * Otherwise, a local-device fallback of 9:00 AM is used to ensure users have access 
 * to their dashboard throughout the disembarkation morning.
 */
export const getCruiseEndTime = (cruise: Pick<Cruise, 'returnDate' | 'ports'>): Date => {
  const returnDateObj = parseISO(cruise.returnDate); // local midnight on return date
  const returnPort = cruise.ports?.find(p => p.date === cruise.returnDate);
  
  let endHours = 9;
  let endMinutes = 0;

  if (returnPort && returnPort.arrivalTime) {
    const timeMatch = returnPort.arrivalTime.match(/(\d+):(\d+)\s*(AM|PM|am|pm)?/);
    if (timeMatch) {
      let h = parseInt(timeMatch[1], 10);
      const m = parseInt(timeMatch[2], 10);
      const ampm = timeMatch[3]?.toLowerCase();
      if (ampm === 'pm' && h < 12) h += 12;
      if (ampm === 'am' && h === 12) h = 0;
      
      // Use port arrival time only if it's later than 9:00 AM
      const portTimeInMinutes = h * 60 + m;
      const defaultTimeInMinutes = 9 * 60;
      
      if (portTimeInMinutes > defaultTimeInMinutes) {
        endHours = h;
        endMinutes = m;
      }
    }
  }

  const returnDateTime = new Date(returnDateObj);
  // Setting hours relative to device local time
  returnDateTime.setHours(endHours, endMinutes, 0, 0);
  return returnDateTime;
};

/**
 * Validates if the current time dictates the ship is actively sailing.
 */
export const isCruiseCurrentlySailing = (
  cruise: Pick<Cruise, 'departureDate' | 'returnDate' | 'ports'>,
  now: Date = new Date()
): boolean => {
  const departureDate = startOfDay(parseISO(cruise.departureDate));
  const returnDateTime = getCruiseEndTime(cruise);

  return now >= departureDate && now < returnDateTime;
};